// src/Context/ToastContext.tsx
// Lightweight app-wide toast system. Replaces alert() and silent console.error
// calls with a visible, dismissable message.
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

export type ToastKind = "info" | "success" | "error";

export interface Toast {
  id: number;
  kind: ToastKind;
  title: string;
  body?: string;
  /** ms before auto-dismiss. 0 = sticky. */
  duration: number;
}

interface ToastContextType {
  toasts: Toast[];
  push: (toast: Omit<Toast, "id" | "duration"> & { duration?: number }) => number;
  dismiss: (id: number) => void;
  /** Convenience helpers */
  error: (title: string, body?: string) => number;
  success: (title: string, body?: string) => number;
  info: (title: string, body?: string) => number;
}

const ToastContext = createContext<ToastContextType | null>(null);

/**
 * Safe hook: components rendered outside the provider (tests, admin) get a
 * no-op implementation instead of a crash.
 */
export const useToast = (): ToastContextType => {
  const ctx = useContext(ToastContext);
  return ctx ?? NOOP_TOAST;
};

const NOOP_TOAST: ToastContextType = {
  toasts: [],
  push: () => -1,
  dismiss: () => {},
  error: () => -1,
  success: () => -1,
  info: () => -1,
};

const DEFAULT_DURATION: Record<ToastKind, number> = {
  info: 4000,
  success: 3500,
  error: 6000,
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback<ToastContextType["push"]>(
    ({ kind, title, body, duration }) => {
      const id = nextId.current++;
      const ms = duration ?? DEFAULT_DURATION[kind];
      setToasts((prev) => {
        // Collapse exact duplicates so a retry loop doesn't stack 10 toasts.
        if (prev.some((t) => t.title === title && t.body === body)) return prev;
        return [...prev.slice(-3), { id, kind, title, body, duration: ms }];
      });
      if (ms > 0) {
        window.setTimeout(() => dismiss(id), ms);
      }
      return id;
    },
    [dismiss],
  );

  const value = useMemo<ToastContextType>(
    () => ({
      toasts,
      push,
      dismiss,
      error: (title, body) => push({ kind: "error", title, body }),
      success: (title, body) => push({ kind: "success", title, body }),
      info: (title, body) => push({ kind: "info", title, body }),
    }),
    [toasts, push, dismiss],
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
};

export default ToastContext;
